<template>
  <span
    class="play-tooltip"
    @mouseenter="onEnter"
    @mouseleave="onLeave"
    @focusin="onFocusIn"
    @focusout="onFocusOut"
  >
    <slot />

    <transition name="play-tooltip-fade">
      <span
        v-if="isOpen"
        class="play-tooltip__bubble"
        :class="[`play-tooltip__bubble--${placement}`, `play-tooltip__bubble--${variant}`]"
        role="tooltip"
      >
        <slot name="content">{{ content }}</slot>
      </span>
    </transition>
  </span>
</template>

<script>
/**
 * PlayTooltip — hover/focus hint for the Play system.
 *
 * Token-driven (see src/styles/tokens.scss). Replaces bootstrap-vue's
 * <b-tooltip> / v-b-tooltip. Self-contained wrapper: it wraps its trigger in the
 * default slot (no id-`target` plumbing) and pops a bubble on hover/focus. Drive
 * the text with `content` or the `content` slot. `trigger="manual"` + the `show`
 * prop gives programmatic control (mirrors b-tooltip's manual mode).
 *
 * VUE3-MIGRATION: transition classes use Vue 2 names (`-enter`/`-leave`); Vue 3
 * renames them to `-enter-from`/`-leave-from` (see the <style> block). Props/
 * slots are already Vue 3-safe.
 */
export default {
  name: 'PlayTooltip',
  props: {
    content: {
      type: String,
      default: '',
    },
    placement: {
      type: String,
      default: 'top',
      validator: v => ['top', 'bottom', 'left', 'right'].includes(v),
    },
    /** hover (+focus) or manual (controlled via `show`) */
    trigger: {
      type: String,
      default: 'hover',
      validator: v => ['hover', 'manual'].includes(v),
    },
    /** Controlled visibility for trigger="manual" */
    show: {
      type: Boolean,
      default: false,
    },
    /** Bubble color role */
    variant: {
      type: String,
      default: 'ink',
      validator: v => ['ink', 'primary', 'secondary', 'highlight', 'accent', 'danger'].includes(v),
    },
  },
  data() {
    return {
      hovered: false,
    }
  },
  computed: {
    isOpen() {
      return this.trigger === 'manual' ? this.show : this.hovered
    },
  },
  methods: {
    onEnter() {
      if (this.trigger === 'hover') this.hovered = true
    },
    onLeave() {
      if (this.trigger === 'hover') this.hovered = false
    },
    onFocusIn() {
      if (this.trigger === 'hover') this.hovered = true
    },
    onFocusOut() {
      if (this.trigger === 'hover') this.hovered = false
    },
  },
}
</script>

<style lang="scss" scoped>
.play-tooltip {
  position: relative;
  display: inline-flex;

  &__bubble {
    --tt-bg: var(--surface-ink);
    --tt-fg: var(--text-on-ink);
    position: absolute;
    z-index: 1060;
    max-width: 240px;
    padding: 7px 11px;
    font-family: var(--font-body);
    font-weight: 600;
    font-size: 12.5px;
    line-height: 1.35;
    white-space: normal;
    text-align: center;
    color: var(--tt-fg);
    background: var(--tt-bg);
    border-radius: var(--radius-sm);
    box-shadow: var(--shadow-hard-sm);
    pointer-events: none;

    /* arrow */
    &::after {
      content: '';
      position: absolute;
      width: 8px;
      height: 8px;
      background: var(--tt-bg);
      transform: rotate(45deg);
    }
  }

  /* color roles */
  &__bubble--primary   { --tt-bg: var(--c-primary);   --tt-fg: var(--c-primary-on); }
  &__bubble--secondary { --tt-bg: var(--c-secondary); --tt-fg: var(--c-secondary-on); }
  &__bubble--highlight { --tt-bg: var(--c-highlight); --tt-fg: var(--c-highlight-on); }
  &__bubble--accent    { --tt-bg: var(--c-accent);    --tt-fg: var(--c-accent-strong); }
  &__bubble--danger    { --tt-bg: var(--c-danger);    --tt-fg: var(--c-danger-on); }

  /* ── Placement ── */
  &__bubble--top {
    bottom: calc(100% + 9px);
    left: 50%;
    transform: translateX(-50%);
    &::after { top: 100%; left: 50%; margin: -4px 0 0 -4px; }
  }
  &__bubble--bottom {
    top: calc(100% + 9px);
    left: 50%;
    transform: translateX(-50%);
    &::after { bottom: 100%; left: 50%; margin: 0 0 -4px -4px; }
  }
  &__bubble--left {
    right: calc(100% + 9px);
    top: 50%;
    transform: translateY(-50%);
    &::after { left: 100%; top: 50%; margin: -4px 0 0 -4px; }
  }
  &__bubble--right {
    left: calc(100% + 9px);
    top: 50%;
    transform: translateY(-50%);
    &::after { right: 100%; top: 50%; margin: -4px -4px 0 0; }
  }
}

/* ── Transition (Vue 2 class names — see VUE3-MIGRATION) ── */
.play-tooltip-fade-enter-active,
.play-tooltip-fade-leave-active {
  transition: opacity var(--dur-fast) var(--ease-out);
}

.play-tooltip-fade-enter,
.play-tooltip-fade-leave-to { opacity: 0; }

@media (prefers-reduced-motion: reduce) {
  .play-tooltip-fade-enter-active,
  .play-tooltip-fade-leave-active { transition: none; }
}
</style>
